import {
  Navigate,
  RouterProvider,
  createBrowserRouter,
} from "react-router-dom";
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { ACCESS_QUERY_KEY } from "./service/access";
import { FetchError } from "./utils/error";

import { adminRoutes } from "./pages/admin/Route";
import { botRoutes } from "./pages/bot/Route";
import {
  adminPinSetupRoutes,
  selectProfileRoutes,
  forgetPasswordRoutes,
  interiorPlanRoutes,
  loginRoutes,
  planRoutes,
  registrationRoutes,
} from "./pages/login/Route";
import { demoBotRoutes } from "./pages/demoBot/Route";
import AuthProvider from "./provider/AuthProvider";
import { urls } from "./urls";
import { ErrorPage } from "./pages/error/ErrorPage";
import { sharedAdminRoutes } from "./pages/admin/SharedRoute";
import { moodBoardRoutes, moodBoardV3Routes } from "./pages/moodBoard/Route";
import TagManager from "react-gtm-module";
import { demoMoodBoardRoutes } from "./pages/demoMoodBoard/Route";
import { outerInteriorPlanRoutes, outerPlanRoutes } from "./pages/plan/Route";
import { embedRoutes } from "./pages/embed/Route";

const tagManagerArgs = {
  gtmId: "GTM-TTZMPHTS",
};

TagManager.initialize(tagManagerArgs);

const router = createBrowserRouter([
  {
    element: <AuthProvider />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: "/",
        element: <Navigate to={urls.login} replace />,
      },
      loginRoutes,
      registrationRoutes,
      planRoutes,
      interiorPlanRoutes,
      botRoutes,
      adminRoutes,
      moodBoardRoutes,
      moodBoardV3Routes,
      forgetPasswordRoutes,
      adminPinSetupRoutes,
      selectProfileRoutes,
    ],
  },
  embedRoutes,
  sharedAdminRoutes,
  demoBotRoutes,
  demoMoodBoardRoutes,
  outerPlanRoutes,
  outerInteriorPlanRoutes,
]);

// 後端回 403（子帳號沒權限、或別台改了 PIN 讓這台的 Admin 失效）→ 重抓目前身分，畫面跟著更新
const refreshAccessOn403 = (error: unknown, query?: unknown) => {
  const key = (query as { queryKey?: unknown[] } | undefined)?.queryKey;
  if (key?.[0] === ACCESS_QUERY_KEY[0]) return;
  if (error instanceof FetchError && error.res.status === 403) {
    queryClient.invalidateQueries({ queryKey: ACCESS_QUERY_KEY });
  }
};

const queryClient: QueryClient = new QueryClient({
  queryCache: new QueryCache({ onError: refreshAccessOn403 }),
  mutationCache: new MutationCache({
    onError: (error) => refreshAccessOn403(error),
  }),
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}

export default App;
